import { useState } from "react";
import { ChevronDown, Download } from "lucide-react";
import { RULES_CONTENT } from "./RulesContent";
import { BIRTHDAYS } from "@/data/birthdays";

const SCORING_TABLE = [
  ["Birdie", "1 dot"],
  ["Eagle / Hole-in-One", "3 dots"],
  ["Sandy Par", "1 dot"],
  ["Hazard Save", "1 dot"],
  ["Closest to Pin on a Par 3, par or better", "1 dot"],
  ["Low score on a hole, unmatched", "1 additional dot"],
];

const SUBSECTIONS = [
  "Money",
  "Dots & Scoring",
  "Par In (Holes 15-18)",
  "Rabbit & Squirrel",
  "Payouts",
  "Relief Rules",
  // Birthdays only shows when birthdays.js has names listed.
  ...(BIRTHDAYS.names.length > 0 ? ["Birthdays"] : []),
];

const ScoringTable = () => (
  <div className="overflow-hidden rounded-lg border border-cypress-gold/20" data-testid="scoring-table">
    <table className="w-full border-collapse text-sm">
      <tbody>
        {SCORING_TABLE.map(([what, dots], i) => (
          <tr key={what} className={i % 2 === 0 ? "bg-cypress-green/40" : "bg-white/[0.03]"}>
            <td className="px-4 py-2.5">{what}</td>
            <td className="px-4 py-2.5 text-right font-mono font-bold text-cypress-gold">{dots}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Subsection = ({ title, open, onToggle, children }) => (
  <div
    className="rounded-xl border border-cypress-gold/20 bg-cypress-green/30"
    data-testid={`rules-subsection-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
  >
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      data-testid={`rules-toggle-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left sm:px-5"
    >
      <h3 className="text-sm font-bold uppercase tracking-[0.16em] text-cypress-gold">
        {title === "Birthdays" ? `${BIRTHDAYS.emoji.heading[0]} ${title} ${BIRTHDAYS.emoji.heading[1]}` : title}
      </h3>
      <ChevronDown className={`h-5 w-5 shrink-0 text-cypress-gold/60 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
    </button>
    {open && <div className="space-y-4 border-t border-cypress-gold/15 px-4 py-4 sm:px-5">{children}</div>}
  </div>
);

export const RulesSection = () => {
  const [openMap, setOpenMap] = useState(() =>
    Object.fromEntries(SUBSECTIONS.map((t) => [t, typeof window !== "undefined" && window.innerWidth >= 768]))
  );

  const toggle = (title) => setOpenMap((m) => ({ ...m, [title]: !m[title] }));

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-10">
      <p className="text-xs font-bold uppercase tracking-[0.3em] text-cypress-gold">Our Game</p>
      <h2 className="mt-1 text-2xl font-bold tracking-tight sm:text-3xl">How the boys settle up</h2>

      <div className="mt-6 flex items-center gap-4 rounded-xl border border-cypress-gold/20 bg-cypress-green/30 px-4 py-4 sm:px-5" data-testid="coordinator-credit">
        <img
          src="/images/nick.jpg"
          alt="Portrait of Nick Sivil, Cypress Boys group coordinator"
          loading="lazy"
          className="h-20 w-20 rounded-full border-2 border-cypress-gold object-cover"
        />
        <div>
          <p className="font-bold tracking-tight">Nick Sivil</p>
          <p className="text-sm text-cypress-cream/60">Group Coordinator</p>
          <p className="mt-1 text-xs italic text-cypress-cream/40">Sets the tee times. Takes the blame.</p>
        </div>
      </div>

      <div className="mt-6 space-y-3" data-testid="rules-list">
        {SUBSECTIONS.map((title) => (
          <Subsection key={title} title={title} open={openMap[title]} onToggle={() => toggle(title)}>
            {title === "Dots & Scoring" ? (
              <>
                <ScoringTable />
                <img
                  src="/images/entrance-sign.jpg"
                  alt="The silver Travis AFB Cypress Lakes Golf Course, Lounge & Grill entrance sign"
                  loading="lazy"
                  data-testid="entrance-sign"
                  className="w-full rounded-lg border border-cypress-gold/20 sm:max-w-md"
                />
              </>
            ) : (
              RULES_CONTENT[title]
            )}
          </Subsection>
        ))}
      </div>

      <div className="mt-8 text-center">
        <a
          href="/cypress-boys-one-pager.pdf"
          download="Cypress-Boys-Rules.pdf"
          data-testid="rules-pdf-link"
          className="inline-flex items-center gap-2 text-sm font-semibold text-cypress-gold underline decoration-cypress-gold/50 decoration-2 underline-offset-4 hover:decoration-cypress-gold"
        >
          <Download className="h-4 w-4" />
          Download the printable one-pager
        </a>
      </div>
    </div>
  );
};
